import { createAppClient } from "./backend/appClient.js";
import { createJobsStore } from "./state/jobsStore.js";
import { createQuotaState } from "./state/quotaState.js";
import { createTranscriptUi } from "./ui/transcriptShell.js";
import { createLibraryUi } from "./ui/libraryShell.js";
import { createUploadController } from "./ui/uploadShell.js";
import { attachAppGlobals } from "./ui/appGlobals.js";
import { showDeleteConfirm } from "../../../packages/app-ui/js/confirmModal.js";

export async function mountProductApp({
  shellElements,
  apiBaseUrl,
  clerkClient,
}) {
  const POLL_INTERVAL_MS = 5000;
  const MAX_POLL_DURATION_MS = 30 * 60 * 1000;
  const TOAST_DURATION_MS = 5500;

  const jobsStore = createJobsStore();
  const quotaState = createQuotaState();
  const appClient = createAppClient({
    apiBaseUrl,
    getAuthToken: () => clerkClient.getAuthToken(),
  });

  const libraryRefs = {};
  const transcriptUi = createTranscriptUi({
    document,
    window,
    jobsStore,
    backendAdapter: appClient.getBackendAdapter(),
    libraryRefs,
  });

  const libraryUi = createLibraryUi({
    document,
    window,
    jobsStore,
    transcriptUi,
    onUploadRequest: async (event) => {
      if (event?.type === "refresh") {
        await refreshJobs();
      }
    },
  });

  Object.assign(libraryRefs, libraryUi);

  const transcriptView = transcriptUi.getTranscriptView();
  let isFirstTime = true;

  let jobsPollTimeoutId = null;
  let jobsPollStartedAt = null;

  function hasInFlightJobs() {
    return jobsStore.files.some(
      (file) =>
        file.status === "queued" ||
        file.status === "transcribing" ||
        file.status === "uploading",
    );
  }

  function stopJobsPoll() {
    if (jobsPollTimeoutId == null) return;
    clearTimeout(jobsPollTimeoutId);
    jobsPollTimeoutId = null;
  }

  function scheduleJobsPoll() {
    if (!hasInFlightJobs()) {
      jobsPollStartedAt = null;
      stopJobsPoll();
      return;
    }
    if (jobsPollTimeoutId != null) return;
    if (!jobsPollStartedAt) {
      jobsPollStartedAt = Date.now();
    }
    const elapsed = Date.now() - jobsPollStartedAt;
    if (elapsed > MAX_POLL_DURATION_MS) {
      stopJobsPoll();
      jobsPollStartedAt = null;
      return;
    }
    jobsPollTimeoutId = setTimeout(async () => {
      jobsPollTimeoutId = null;
      try {
        await refreshJobs();
      } catch {
        // refresh_jobs already logs errors
      }
    }, POLL_INTERVAL_MS);
  }

  // track rejected files for batched toast display
  let pendingRejections = [];
  let rejectionToastTimeout = null;

  function showUploadErrorToast() {
    const toast = document.getElementById("uploadErrorToast");
    if (!toast) return;

    const titleEl = toast.querySelector(".upload-error-toast__title");
    const hintEl = toast.querySelector(".upload-error-toast__hint");
    const count = pendingRejections.length;
    const first = pendingRejections[0] || null;

    if (titleEl && hintEl) {
      if (count === 1 && first) {
        const copy = describeUploadRejection(first.reason);
        titleEl.textContent = copy.title;
        hintEl.textContent = copy.hint;
      } else {
        titleEl.textContent = `${count} files skipped`;
        hintEl.textContent = "some files exceeded limits or quota";
      }
    }

    toast.classList.add("show");
    pendingRejections = [];

    setTimeout(() => toast.classList.remove("show"), 4000);
  }

  // quota modal logic
  function showQuotaModal() {
    const backdrop = document.getElementById("quotaModalBackdrop");
    const modal = document.getElementById("quotaModal");
    if (backdrop) backdrop.classList.add("show");
    if (modal) modal.classList.add("show");
  }

  function hideQuotaModal() {
    const backdrop = document.getElementById("quotaModalBackdrop");
    const modal = document.getElementById("quotaModal");
    const copied = document.getElementById("quotaCopied");
    if (backdrop) backdrop.classList.remove("show");
    if (modal) modal.classList.remove("show");
    if (copied) copied.classList.remove("show");
  }

  function copyQuotaEmail() {
    const email = "jack@sifttext.com";
    navigator.clipboard
      .writeText(email)
      .then(() => {
        const copied = document.getElementById("quotaCopied");
        if (copied) {
          copied.classList.add("show");
          setTimeout(() => copied.classList.remove("show"), TOAST_DURATION_MS);
        }
      })
      .catch(() => {
        window.prompt("Copy this email address:", email);
      });
  }

  // bind quota modal buttons
  function bindQuotaModalButtons() {
    const emailBtn = document.getElementById("quotaEmailBtn");
    const dismissBtn = document.getElementById("quotaDismissBtn");
    if (emailBtn) emailBtn.addEventListener("click", copyQuotaEmail);
    if (dismissBtn) dismissBtn.addEventListener("click", hideQuotaModal);
  }
  bindQuotaModalButtons();

  // update upload button state based on quota
  function updateUploadButtonState() {
    const uploadBtn = document.querySelector(".new-upload-btn");
    if (!uploadBtn) return;

    if (quotaState.isOverQuota()) {
      uploadBtn.disabled = true;
      uploadBtn.classList.add("new-upload-btn--disabled");
    } else {
      uploadBtn.disabled = false;
      uploadBtn.classList.remove("new-upload-btn--disabled");
    }
  }

  // intercept upload attempts when over quota
  function handleUploadAttempt(event) {
    if (quotaState.isOverQuota()) {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      showQuotaModal();
      return true;
    }
    return false;
  }

  const uploadController = createUploadController({
    fileInput: shellElements.fileInput,
    dropzone: shellElements.dropzone,
    jobsStore,
    backendAdapter: appClient.getBackendAdapter(),
    isOverQuota: () => quotaState.isOverQuota(),
    onQuotaBlocked: () => showQuotaModal(),
    onJobsUpdated: (event) => {
      if (event?.type === "upload-progress" && event.entry) {
        libraryUi.refreshFileRow(event.entry);
        return;
      }
      if (event?.type === "upload-queued") {
        markReturning();
      }
      if (event?.type === "quota-refresh") {
        fetchQuota();
      }
      if (event?.type === "upload-rejected") {
        // batch rejections for aggregated toast
        pendingRejections.push({
          filename: event.filename,
          reason: event.reason || "unknown",
        });
        clearTimeout(rejectionToastTimeout);
        rejectionToastTimeout = setTimeout(showUploadErrorToast, 100);
        libraryUi.renderFileList();
        libraryUi.applyFileListHeightConstraint();
        updateQuotaBanner(shellElements.quotaBanner, quotaState);
        return;
      }
      libraryUi.renderFileList();
      libraryUi.applyFileListHeightConstraint();
      updateQuotaBanner(shellElements.quotaBanner, quotaState);
      if (event?.type === "server-refresh") {
        refreshJobs().catch((error) =>
          console.error("job_refresh_during_upload_failed", error),
        );
      }
    },
  });

  async function deleteFile(id) {
    const entry =
      jobsStore.files.find((f) => f.id === id) || jobsStore.findByJobId(id);
    if (!entry) return;

    const displayName = entry.displayName || entry.name || "this transcript";
    const confirmed = await showDeleteConfirm(displayName);
    if (!confirmed) return;

    // delete from backend if it has a job id
    if (entry.jobId) {
      try {
        await appClient.getBackendAdapter().deleteJob(entry.jobId);
      } catch (error) {
        console.error("delete_job_failed", error);
        // still remove from local state even if backend fails
      }
    }

    // remove from local state
    jobsStore.removeFileById(entry.id);
    libraryUi.renderFileList();
    libraryUi.applyFileListHeightConstraint();
    updateQuotaBanner(shellElements.quotaBanner, quotaState);
  }

  attachAppGlobals({
    filterFiles: (value) => libraryUi.filterFiles(value),
    setSearchMode: (mode) => libraryUi.setSearchMode(mode),
    sortFiles: () => libraryUi.sortFiles(),
    setFilter: (filter) => libraryUi.setFilter(filter),
    showVendingMachine: () => {
      isFirstTime = true;
      libraryUi.showVendingMachine();
    },
    showLibrary: () => markReturning(),
    handleFileRowClick: (event) => libraryUi.handleFileRowClick(event),
    cancelFile: (id) => uploadController.cancelFile(id),
    retryFile: (id) => uploadController.retryFile(id),
    downloadFile: (id) => uploadController.downloadFile(id),
    deleteFile,
    downloadCurrentTranscript: () => transcriptView.downloadCurrentTranscript(),
    copyCurrentTranscript: () => transcriptView.copyCurrentTranscript(),
    returnToLibrary: () => transcriptView.returnToLibrary?.(),
    saveCurrentTranscript: () => transcriptView.saveCurrentTranscript?.(),
    discardCurrentTranscript: () => transcriptView.discardCurrentTranscript?.(),
    copyFeedbackEmail: () => {
      const email = "jack@sifttext.com";
      navigator.clipboard
        .writeText(email)
        .then(() => {
          const toast = document.getElementById("feedbackToast");
          if (toast) {
            toast.classList.add("show");
            setTimeout(() => toast.classList.remove("show"), TOAST_DURATION_MS);
          }
        })
        .catch(() => {
          // fallback: prompt user to copy manually
          window.prompt("Copy this email address:", email);
        });
    },
  });

  // mount user buttons in both header and vending machine
  clerkClient.mountUserButton(shellElements.userButton);
  clerkClient.mountUserButton(shellElements.vmUserButton);

  // remove placeholder once clerk actually renders (async)
  [shellElements.userButton, shellElements.vmUserButton]
    .filter(Boolean)
    .forEach((container) => {
      const placeholder = container.querySelector(".clerk-button-placeholder");
      if (placeholder) {
        const observer = new MutationObserver((mutations, obs) => {
          const hasClerkContent = Array.from(container.children).some(
            (el) => !el.classList.contains("clerk-button-placeholder"),
          );
          if (hasClerkContent) {
            placeholder.remove();
            obs.disconnect();
          }
        });
        observer.observe(container, { childList: true });
      }
    });

  // load cached jobs immediately (synchronous, ~2ms) for instant render
  const hadCache = jobsStore.loadFromCache();
  if (hadCache) {
    isFirstTime = false;
    console.log(
      `[sift-perf] cache-hit: ${jobsStore.files.length} jobs from localStorage`,
    );
  }

  // fetch quota in background and update banner when ready
  fetchQuota();

  // show appropriate view based on cache state
  // - returning users (has cache): show library with cached data
  // - first-time users (no cache): show vending machine immediately
  if (hadCache) {
    libraryUi.showLibrary();
    libraryUi.renderFileList();
    libraryUi.applyFileListHeightConstraint();
  } else {
    libraryUi.showVendingMachine();
  }

  // fire-and-forget session - not needed for initial render
  ensureSession(appClient);

  // fetch fresh data in background - don't block initial render
  // UI will update when fresh data arrives
  refreshJobs()
    .then(() => {
      applyEntryShell();
      // prefetch first 3 recent transcripts for instant access
      transcriptUi.prefetchRecent();
    })
    .catch((error) => {
      console.error("initial_jobs_refresh_failed", error);
      // still show entry shell even if refresh fails
      applyEntryShell();
    });

  return {
    async reloadJobs() {
      await refreshJobs();
    },
  };

  function markReturning() {
    if (isFirstTime) {
      isFirstTime = false;
    }
    libraryUi.showLibrary();
  }

  function applyEntryShell() {
    if (isFirstTime) {
      libraryUi.showVendingMachine();
    } else {
      libraryUi.showLibrary();
    }
  }

  async function ensureSession(client) {
    try {
      await client.fetchSession();
    } catch (error) {
      console.error("session_fetch_failed", error);
    }
  }

  async function fetchQuota() {
    try {
      const data = await appClient.getBackendAdapter().getQuota();
      quotaState.setQuota(data);
      updateQuotaBanner(shellElements.quotaBanner, quotaState);
      updateUploadButtonState();
    } catch (error) {
      console.error("quota_fetch_failed", error);
    }
  }

  async function refreshJobs() {
    try {
      const page = await appClient.listJobs({ limit: 50 });
      jobsStore.replaceWithJobs(page.jobs || []);
      // set first-time state based on whether user has any jobs
      // handles both: new jobs arriving AND all cached jobs deleted server-side
      isFirstTime = jobsStore.files.length === 0;
      libraryUi.renderFileList();
      libraryUi.applyFileListHeightConstraint();
      if (hasInFlightJobs()) {
        scheduleJobsPoll();
      } else {
        jobsPollStartedAt = null;
        stopJobsPoll();
      }
    } catch (error) {
      console.error("jobs_refresh_failed", error);
    } finally {
      // refresh quota alongside jobs for live used + queued display
      await fetchQuota();
    }
  }
}

function updateQuotaBanner(quotaBanner, quotaState) {
  if (!quotaBanner) return;
  if (!quotaState?.isLoaded?.()) return;

  const used = quotaState.getUsedMinutes();
  const included = quotaState.getIncludedMinutes();
  const isNearing = quotaState.isNearingLimit();
  const isOver = quotaState.isOverQuota();

  // update text with warning icon if nearing/over limit
  const prefix = isNearing || isOver ? "⚠ " : "";
  quotaBanner.textContent = `${prefix}${used} / ${included} minutes used`;

  // update styling
  quotaBanner.classList.remove(
    "quota-banner--warning",
    "quota-banner--exceeded",
  );
  if (isOver) {
    quotaBanner.classList.add("quota-banner--exceeded");
  } else if (isNearing) {
    quotaBanner.classList.add("quota-banner--warning");
  }
}

function describeUploadRejection(reason) {
  switch (reason) {
    case "invalid_type":
      return {
        title: "unsupported file type",
        hint: "try audio or video formats",
      };
    case "file_too_large":
      return { title: "file too large", hint: "max size is 100GB" };
    case "duration_too_long":
      return { title: "file too long", hint: "max duration is 12 hours" };
    case "duration_unreadable":
      return { title: "can't read duration", hint: "try a different file" };
    case "quota_exceeded":
      return { title: "quota exceeded", hint: "monthly minutes limit reached" };
    default:
      return { title: "file skipped", hint: "upload couldn't be processed" };
  }
}
